import { useQuery } from '@apollo/client'
import { Call, Device } from '@twilio/voice-sdk'
import { debounce } from 'lodash'
import {
  createContext,
  Dispatch,
  FC,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Person } from 'src/@types/graphql'
import { useUser } from 'src/contexts/providers/UserProvider'
import { TWILLIO_ACCESS_TOKEN_EXP } from 'src/helpers'
import { createLogger } from 'src/logger'
import { GET_VOICE_TOKEN } from './data/queries'

const logger = createLogger('VoiceProvider')

interface PhonePerson {
  type: string
  first_name: string
  last_name: string
  person_id: string
  property_name: string
  property_phone: string
  conference_id: string
  call_intent?: string
  call_type?: string
}

interface ACall {
  active: boolean
  status: string
  person: Partial<PhonePerson>
  phone_number: string
  params?: string
  muted: boolean
  callType?: string
  callSid?: string
  transferFromCallSid?: string
  transferFromName?: string
  transferToName?: string
}

const defaultPerson = {
  type: '',
  first_name: '',
  last_name: '',
  person_id: '',
  property_name: '',
  property_phone: '',
  conference_id: '',
  call_type: '',
  call_intent: '',
}

const defaultCall = {
  active: false,
  status: '', // Dial,
  person: defaultPerson,
  phone_number: '',
  muted: false,
  user: { id: 1 },
}

const VoiceContext = createContext<{
  acceptCall: () => void
  call: ACall
  callPerson: ({ person }: { person: Person }) => void
  closeDialer: () => void
  dialCall: ({
    fromNumber,
    toNumber,
    userId,
  }: {
    fromNumber: string
    toNumber: string
    userId?: number
    person?: Partial<PhonePerson>
  }) => void
  endCall: () => void
  minimized: boolean
  muteCall: () => void
  openCallDrawer: boolean
  rejectCall: () => void
  setMinimized: Dispatch<boolean>
  setOpenCallDrawer: Dispatch<boolean>
  openDialer: () => void
  resetTransfer: () => void
  sendDigits: (digitDialed: string) => void
}>({
  acceptCall: () => {},
  call: defaultCall,
  callPerson: () => {},
  closeDialer: () => {},
  dialCall: () => {},
  endCall: () => {},
  minimized: true,
  muteCall: () => {},
  openCallDrawer: false,
  rejectCall: () => {},
  setMinimized: () => {},
  setOpenCallDrawer: () => {},
  openDialer: () => {},
  resetTransfer: () => {},
  sendDigits: () => {},
})
export const useVoice = () => useContext(VoiceContext)

const getPersonFromCallParams = (call: Call | null) => ({
  type: call?.customParameters.get('type') || '',
  first_name: call?.customParameters.get('first_name') || '',
  last_name: call?.customParameters.get('last_name') || '',
  person_id: call?.customParameters.get('person_id') || '',
  property_name: call?.customParameters.get('property_name') || '',
  property_phone: call?.customParameters.get('property_phone') || '',
  conference_id: call?.customParameters.get('conference_id') || '',
  call_type: call?.customParameters.get('call_type') || '',
  call_intent: call?.customParameters.get('call_intent') || '',
})

export const VoiceProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const [openCallDrawer, setOpenCallDrawer] = useState<boolean>(false)
  const [minimized, setMinimized] = useState<boolean>(true)
  const [call, setCall] = useState<ACall>(defaultCall)
  const { data, refetch } = useQuery(GET_VOICE_TOKEN)
  const device = useRef<Device | null>(null)
  const connection = useRef<Call | undefined>()
  const { user } = useUser()
  useEffect(() => {
    if (data?.createVoiceToken) {
      device.current = new Device(data.createVoiceToken, {
        allowIncomingWhileBusy: false,
        // emit 'tokenWillExpire` in number of ms before a token is expired
        // @ts-ignore
        tokenRefreshMs: TWILLIO_ACCESS_TOKEN_EXP,
      })
      device.current.register()
      device.current.on('incoming', (connect) => {
        logger.info({
          msg: 'incoming call',
          callSid: connect?.parameters.CallSid,
          params: [...connect?.customParameters.entries()],
        })
        const person = getPersonFromCallParams(connect)
        connection.current = connect
        connect.on('cancel', () => {
          setOpenCallDrawer(false)
          setCall(defaultCall)
          logger.info({
            msg: 'call cancel',
          })
        })
        connect.on('disconnect', () => {
          setOpenCallDrawer(false)
          setCall(defaultCall)
          logger.info({
            msg: 'call disconnect',
          })
        })
        connect?.on('accept', () => {
          setCall((prev) => ({
            ...prev,
            status: 'Recording...',
          }))
          logger.info({
            msg: 'call accepted',
            callSid: connect?.parameters.CallSid,
          })
        })
        setCall((prev) => ({
          ...prev,
          active: true,
          muted: false,
          person,
          phone_number: connect.parameters.From,
          params: connect?.parameters.Params,
          status: 'Ringing...',
          callSid: connect?.parameters.CallSid,
          callType: connect?.customParameters.get('callType') || '',
          transferFromCallSid:
            connect?.customParameters.get('fromCallSid') || '',
          transferFromName: connect?.customParameters.get('fromName') || '',
          transferToName: connect?.customParameters.get('toName') || '',
        }))
        setOpenCallDrawer(true)
      })

      // get a new auth token
      device.current.on('tokenWillExpire', () => {
        logger.info('token will expire')
        refetch().then(() => {
          logger.info('token refreshed')
          device.current?.updateToken(data?.createVoiceToken)
        })
      })
    }
  }, [data?.createVoiceToken, refetch])

  const resetTransfer = useCallback(() => {
    setCall((prev) => ({
      ...prev,
      transferFromCallSid: undefined,
      transferToName: undefined,
      transferFromName: undefined,
      callType: undefined,
    }))
  }, [])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sendDigits = useCallback(
    debounce(
      (digitDialed: string) => {
        connection.current?.sendDigits(digitDialed)
      },
      300,
      {
        leading: true,
        trailing: true,
      },
    ),
    [],
  )

  const closeDialer = useCallback(() => {
    setOpenCallDrawer(false)
    setMinimized(true)
    logger.info({ msg: 'close dialer' })
  }, [])

  const dialCall = useCallback(
    async ({
      fromNumber,
      toNumber,
      userId,
      person,
    }: {
      fromNumber: string
      toNumber: string
      userId?: number
      person?: Partial<PhonePerson>
    }) => {
      logger.info({ msg: 'start outgoing call', fromNumber, toNumber, userId })

      connection.current = await device.current?.connect({
        params: {
          fromNumber,
          toNumber,
          userId: String(userId),
        },
      })

      /**
       * @todo fetch person based on number being called
       */
      setCall((prev) => ({
        ...prev,
        active: true,
        person: { ...defaultPerson, ...(person || {}) },
        phone_number: toNumber,
        status: 'Ringing...',
      }))
      connection?.current?.on('ringing', () => {
        setCall((prev) => ({
          ...prev,
          status: 'Ringing...',
          callSid: connection.current?.parameters?.CallSid || '',
          person: {
            ...prev.person,
            conference_id: connection.current?.parameters.CallSid || '',
          },
        }))
      })
      connection?.current?.on('accept', () => {
        setCall((prev) => ({
          ...prev,
          status: 'Recording...',
        }))
      })
      connection?.current?.on('cancel', () => {
        setCall(defaultCall)
        closeDialer()
      })
      connection?.current?.on('disconnect', async () => {
        setCall(defaultCall)
        closeDialer()
      })
    },
    [closeDialer],
  )

  const openDialer = (minimized = false) => {
    setMinimized(minimized)
    setOpenCallDrawer(true)
  }

  const muteCall = useCallback(async () => {
    if (connection.current?.isMuted()) {
      connection.current.mute(false)

      setCall((prev) => ({
        ...prev,
        muted: false,
        status: 'Recording...',
      }))
    } else {
      connection?.current?.mute(true)
      setCall((prev) => ({
        ...prev,
        muted: true,
        status: 'Recording...',
      }))
    }
  }, [])

  const endCall = useCallback(async () => {
    device.current?.disconnectAll()
    setOpenCallDrawer(false)
    setCall(defaultCall)
    logger.info({ msg: 'end call' })
  }, [])

  const acceptCall = useCallback(async () => {
    connection?.current?.accept()
    setCall((prev) => ({
      ...prev,
      active: true,
      muted: false,
      status: 'Recording...',
    }))
  }, [])

  const rejectCall = useCallback(async () => {
    if (connection.current?.direction === 'OUTGOING') {
      connection?.current?.disconnect()
    } else {
      connection?.current?.reject()
    }
    setOpenCallDrawer(false)
    setCall(defaultCall)
    logger.info({
      msg: 'reject call',
      direction: connection.current?.direction,
    })
  }, [])

  const callPerson = useCallback(
    async ({ person }: { person: Person }) => {
      const phone = person?.phones.find((phn) => phn.is_default)
      await dialCall({
        person: {
          type: person.type,
          first_name: person.first_name || phone?.phone_number || '',
          last_name: person.last_name || '',
          person_id: String(person.id) || '',
          property_name: person?.sticky_number?.property?.name || '',
          property_phone: person?.sticky_number?.phone_number || '',
          conference_id: '',
        },
        fromNumber: person?.sticky_number?.phone_number!,
        toNumber: phone?.phone_number!,
        userId: user?.id!,
      })

      openDialer(true)
    },
    [dialCall, user?.id],
  )

  const values = useMemo(
    () => ({
      acceptCall,
      call,
      callPerson,
      closeDialer,
      dialCall,
      endCall,
      minimized,
      muteCall,
      openCallDrawer,
      openDialer,
      rejectCall,
      setMinimized,
      setOpenCallDrawer,
      resetTransfer,
      sendDigits,
    }),
    [
      acceptCall,
      call,
      callPerson,
      closeDialer,
      dialCall,
      endCall,
      minimized,
      muteCall,
      openCallDrawer,
      rejectCall,
      resetTransfer,
      sendDigits,
    ],
  )

  // DEBUG
  // console.log("currently active token", device.current?.token)

  return (
    <VoiceContext.Provider value={values}>{children}</VoiceContext.Provider>
  )
}
